import { useEffect, useRef, useState } from 'react';

interface VoiceButtonProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  isRecordingActive?: (active: boolean) => void;
}

interface SpeechRecognitionResult {
  readonly length: number;
  readonly isFinal: boolean;
  [index: number]: { transcript: string; confidence: number };
}

interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition: new () => SpeechRecognitionInstance;
  }
}

export default function VoiceButton({ onTranscript, disabled = false, isRecordingActive }: VoiceButtonProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [interimText, setInterimText] = useState('');
  const [audioLevel, setAudioLevel] = useState<number[]>([3, 5, 4, 6, 3]);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const finalTextRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioLevelTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'ja-JP';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsRecording(true);
      finalTextRef.current = '';
      setInterimText('');
      isRecordingActive?.(true);

      audioLevelTimerRef.current = setInterval(() => {
        setAudioLevel([
          Math.floor(Math.random() * 8) + 2,
          Math.floor(Math.random() * 12) + 4,
          Math.floor(Math.random() * 16) + 6,
          Math.floor(Math.random() * 12) + 4,
          Math.floor(Math.random() * 8) + 2,
        ]);
      }, 120);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = finalTextRef.current;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      finalTextRef.current = final;
      setInterimText(interim);

      if (final) {
        onTranscript(final + interim);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error !== 'aborted') {
        console.error('音声認識エラー:', event.error);
      }
      stopTimers();
      setIsRecording(false);
      setInterimText('');
      isRecordingActive?.(false);
    };

    recognition.onend = () => {
      stopTimers();
      setIsRecording(false);
      setInterimText('');
      isRecordingActive?.(false);
      if (finalTextRef.current.trim()) {
        onTranscript(finalTextRef.current.trim());
      }
      finalTextRef.current = '';
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.abort();
      stopTimers();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (audioLevelTimerRef.current) {
      clearInterval(audioLevelTimerRef.current);
      audioLevelTimerRef.current = null;
    }
  };

  const handleClick = () => {
    if (disabled) return;
    if (isRecording) {
      recognitionRef.current?.stop();
    } else {
      try {
        recognitionRef.current?.start();
      } catch (_e) {
        // already started
      }
    }
  };

  if (!isSupported) return null;

  return (
    <div className="flex-shrink-0 flex flex-col items-center gap-1">
      {/* マイクボタン本体 */}
      <div className="relative flex items-center justify-center">
        {isRecording && (
          <span className="absolute w-10 h-10 rounded-full bg-red-400/40 animate-ping" style={{ animationDuration: '1s' }} />
        )}
        <button
          onClick={handleClick}
          disabled={disabled}
          className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap z-10 ${
            isRecording
              ? 'bg-red-500 hover:bg-red-600 shadow-md'
              : 'bg-gray-100 hover:bg-gray-200 active:scale-95'
          } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          aria-label={isRecording ? '録音停止' : '音声入力'}
        >
          <i className={`${isRecording ? 'ri-stop-fill text-white' : 'ri-mic-line text-gray-600'} text-lg`}></i>
        </button>
      </div>

      {/* 録音中：音量バーのみ小さく表示 */}
      {isRecording && (
        <div className="flex items-end gap-[2px] h-3">
          {audioLevel.map((h, i) => (
            <div
              key={i}
              className="w-[2px] bg-red-500 rounded-full transition-all duration-100"
              style={{ height: `${Math.min(h, 12)}px` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
